<template>
  <div class="campaign-details create-campaign-dialog-panel-one details">
    <div class="subtitle">
      1. {{t('Campaign details')}}
    </div>

    <div class="p-16 campaign-details__wrap">
      <div v-bind="getIntroParams(2)">
        <div>
          <workspace-select
            :form="form"
            :labelHelpText="t('Workspace allows to make the required campaign or other element accessible only by a specific member or group of members')"
            :disabled="edit"
            :workspacesList="workspacesList"
            attach
            @change-workspace="onChangeWorkspace"
          ></workspace-select>
        </div>

        <ui-input
          v-model="form.name"
          class="step-z"
          :placeholder="t('Name')"
          :label="t('Name')+'*'"
          :label-help="t('The first part of name is automatically generated basing on country and traffic source selected.')"
          name="name"
          :hide-details="1"
          :error-messages="errorsDict['name']"
        ></ui-input>

        <p class="name-description">{{ nameDescription }}</p>
      </div>

      <div v-bind="getIntroParams(3)">
        <ui-select
          v-model="form.trafficSource"
          :placeholder="t('Traffic source')"
          :label="t('Traffic source')"
          :label-help="t('Select traffic source to add tokens to the Campaign URL automatically.')"
          :items="trafficSourceSelectOptionsWithNone"
          :fetchOptions="fetchTrafficSourcesOptions"
          item-text="name"
          item-value="id"
          :return-object="true"
          :hide-details="2"
          attach
          :error-messages="errorsDict['trafficSource']"
        ></ui-select>
      </div>

      <div>
        <ui-select
          v-model="form.countryId"
          :placeholder="t('Country')"
          :label="t('Country')"
          :label-help="t('Country selection is required to navigate easy through the offers list. This selection does not affect the traffic distribution.')"
          :items="countryListPlusGlobal"
          item-text="name"
          item-value="id"
          :return-object="false"
          :hide-details="2"
          attach
          @change="onChangeCountry"
          :error-messages="errorsDict['countryId']"
        ></ui-select>
      </div>
      <div :class="{'plan-limits-warning__tags': showPlanLimitsWarning_tags}">
        <ui-select
          planLimits="tagsCampaigns"
          v-model="form.tags"
          :placeholder="t('Select or type tags...')"
          :label="t('Tags')"
          :label-help="t('Add personalized tags to search and navigate easy through campaign list.')"
          :items="store.cache.tags.campaigns.data"
          :hide-details="2"
          multiple
          item-text="name"
          chips
          attach
          combobox
          multi-line
          :error-messages="errorsDict['tags']"
        >
          <template #no-data>
            <no-tag-found></no-tag-found>
          </template>
        </ui-select>
        <button v-if="showPlanLimitsWarning_tags" class="plan-limits-warning__clear" @click="clearTags">
          {{t('Reset tags')}}
        </button>
        <ui-tooltip-icon
          class="plan-limits-warning__tooltip"
          preset="error"
          :text="t('This option is no longer active due to the subscription change to the plan which doesn’t include this option or this add-on is switched off in your subscriptions settings.')"
          v-if="showPlanLimitsWarning_tags"
        ></ui-tooltip-icon>
      </div>

      <div>
        <ui-select
          :value="form.trackingDomainId"
          @change="onChangeTrackingDomain"
          :placeholder="t('Account default')"
          :label="t('Tracking domain')"
          :label-help="t('Choose domain to run your campaign. Click Domains tab in Settings to add other domains.')"
          :items="trackingDomains"
          item-text="domain"
          item-value="id"
          :return-object="false"
          :hide-details="2"
          attach
          :error-messages="errorsDict['trackingDomainId']"
        ></ui-select>
      </div>
  
      <div class="mb-12"
        :class="{
          'plan-limits-warning__uniquenessPeriod': showPlanLimitsWarning_uniqness
        }"
      >
        <ui-tooltip-icon
          class="plan-limits-warning__tooltip"
          preset="error"
          v-if="showPlanLimitsWarning_uniqness"
          :text="t('This option is no longer active due to the subscription change to the plan which doesn’t include this option or this add-on is switched off in your subscriptions settings.')"
        ></ui-tooltip-icon>
        <button v-if="showPlanLimitsWarning_uniqness" class="plan-limits-warning__clear" @click="resetUniquenessPeriod">
          {{t('Reset')}}
        </button>
        <ui-count-input
          v-model="uniquenessPeriod"
          :min="0"
          :max="744"
          planLimits="uniquenessPeriod"
          :has-errors="!!errorsDict['uniquenessPeriod']"
          :planLimitsEditable="form.uniquenessPeriod !== defaultForm.uniquenessPeriod"
          :label="t('Uniqueness period (hours)')"
          :helpTooltip="`${t('Set the period in which the traffic should be displayed as unique for the campaign.')} <br> ${t('This configuration is required to set up uniqueness rules for traffic distribution.')}`"
          class="uniquenessPeriod"
        />
      </div>

      <div class="mb-12" v-if="FFconversionNotificationUrlEnabled">
        <ui-input
          v-model="form.conversionNotificationUrl"
          :placeholder="t('Conversion Notification URL')"
          :label="t('Conversion Notification URL')"
          :label-help="t('Enter an additional URL to send conversions to another service.')"
          :hide-details="1"
          :error-messages="errorsDict['conversionNotificationUrl']"
        ></ui-input>
      </div>

      <div :class="{'plan-limits-warning__sendPostback':showPlanLimitsWarning_usePostback}">
        <ui-tooltip-icon
          v-if="showPlanLimitsWarning_usePostback"
          class="plan-limits-warning__tooltip"
          :text="t('This option is no longer active due to the subscription change to the plan which doesn’t include this option or this add-on is switched off in your subscriptions settings.')"
          preset="error"
        ></ui-tooltip-icon>
        <button v-if="showPlanLimitsWarning_usePostback" class="plan-limits-warning__clear" @click="resetPostback">
          {{t('Reset')}}
        </button>
        <ui-checkbox
          class="checkbox-container"
          planLimits="sendPostbacksCampaign"
          :label="t('Conversions Shaving %<br> (Previously Named Send Postbacks, %)')"
          :help-tooltip="t('By default, BeMob sends 100% of conversions to your traffic source. Use this option to send only a selected percentage of conversions if you do not want to disclose the full conversion volume. The value set here applies to this campaign only.')"
          v-model="usePostback"
          :planLimitsEditable="usePostback"
        ></ui-checkbox>
        <div v-if="usePostback">
          <div style="display: flex;">
            <ui-input
              planLimits="sendPostbacksCampaign"
              v-model="postbackPercent"
              suffix="%"
              :max="100"
              :has-errors="!!errorsDict['postbackPercent']"
              :planLimitsEditable="usePostback"
            ></ui-input>
          </div>
        </div>
      </div>

      <div :class="{'plan-limits-warning__trafficLoss': showPlanLimitsWarning_useTrafficLoss}">
        <ui-tooltip-icon
          class="plan-limits-warning__tooltip"
          preset="error"
          :text="t('This option is no longer active due to the subscription change to the plan which doesn’t include this option or this add-on is switched off in your subscriptions settings.')"
          v-if="showPlanLimitsWarning_useTrafficLoss"
        ></ui-tooltip-icon>
        <button v-if="showPlanLimitsWarning_useTrafficLoss" class="plan-limits-warning__clear" @click="resetTrafficLoss">
          {{t('Reset')}}
        </button>
        <ui-checkbox
          planLimits="trafficLossCampaign"
          class="checkbox-container"
          :label="`${t('Traffic loss')}, %`"
          :help-tooltip="t('Traffic loss % set at the level of campaign will override the % in the traffic source settings.')"
          v-model="useTrafficLoss"
          :planLimitsEditable="useTrafficLoss"
        ></ui-checkbox>
        <div v-if="useTrafficLoss">
          <div style="display: flex;">
            <ui-input
              suffix="%"
              planLimits="trafficLossCampaign"
              v-model="trafficLossPercent"
              :max="100"
              :planLimitsEditable="useTrafficLoss"
              :has-errors="!!errorsDict['trafficLossPercent']"
            ></ui-input>
          </div>
        </div>
      </div>
  
      <ui-label
        class="mb-4 mt-4"
        :text="t('Send Custom Conversions')"
        :help-tooltip="t('This option allows sending the particular custom event to the side of the traffic source.')"
      ></ui-label>

      <ui-radio-group v-model="form.sendCustomConversionMode" styleType="inline" class="mb-4">
        <ui-radio
          :helpTooltip="t('All the custom conversions will be sent except the conversions for which sending via postback is not allowed on the level of global custom conversions settings.')"
          class="pr-12 mb-4"
          value="all"
          :label="t('All')"/>
        <ui-radio
          :helpTooltip="t('Mark the conversions you wish to send to the traffic source for this particular campaign. Only marked conversions will be sent.')"
          class="pr-12 mb-4"
          :click="onSendCustomConversionsClick"
          value="include"
          :label="t('Only Selected')"/>
        <ui-radio
          :helpTooltip="t('Mark the conversions you don’t wish to send to the traffic source for this particular campaign. Marked conversions will not be sent.')"
          value="exclude"
          class="mb-4"
          :click="onSendCustomConversionsClick"
          :label="t('Except Selected')"/>
      </ui-radio-group>

      <ui-select
        v-if="form.sendCustomConversionMode!=='all'"
        v-model="form.sendCustomConversionIds"
        :items="customConversions"
        item-text="name"
        item-value="id"
        :hide-details="2"
        :return-object="false"
        multiple
        chips
        attach
        multi-line
        :error-messages="errorsDict['sendCustomEventPostback']"
      ></ui-select>

      <ui-checkbox
        v-if="form.sendCustomConversionMode==='all' || form.sendCustomConversionMode==='exclude'"
        v-model="form.doNotSendNotSpecifiedConversions"
        :label="t('Do not send conversions with Not Specified status')"
        :help-tooltip="t('This option allows to limit the sending of custom conversions with \'Not Specified\' status to the traffic  source associated with this campaign.')"
      />

      <ui-select
        v-model="form.userAgentClientHintsEnabled"
        :label="t('Use Client Hints')"
        :label-help="t('Enable to collect additional data on OS, browser version, and device type.<br>This feature may slow down redirects. You can activate it for a specific campaign or globally in General → Tracking OS, Browser and Device Data → Use Client Hints.')"
        :items="useClientHintsItems"
        :return-object="false"
      />

      <ui-select
        v-model="form.botFiltersMode"
        :label="t('Use Bot Filters')"
        :label-help="t('Allows disabling global bot filters for this campaign.')"
        :items="botFiltersModeItems"
        :return-object="false"
      />
      
      <div>
        <ui-select
          v-model="form.currencyId"
          :placeholder="t('Currency')"
          :label="t('Currency')"
          :label-help="t('Select the currency of the traffic cost. Campaign currency will be automatically converted to the account currency selected with registration.')"
          :items="currencyListOrderedPreferred"
          item-text="name"
          item-value="code"
          :return-object="false"
          :hide-details="2"
          attach
          :menu-props="{
            'maxHeight': 190
          }"
          :error-messages="errorsDict['currencyId']"
        >
        </ui-select>
      </div>

      <cost-model
        v-model:currencyId="form.currencyId"
        v-model:costModel="form.costModel"
        v-model:costValue="form.costValue"
        :maximum-cost-available="true"
        v-model:maxCostEnabled="form.maxCostEnabled"
        v-model:maxCostUsd="form.maxCostUsd"
        v-model:maxCostAction="form.maxCostAction"
        v-model:maxCostBotOfferId="form.maxCostBotOfferId"
        :offer-options="offerOptions"
        :fetch-offer-options="fetchOfferOptions"
        :errors="errorsDict['costValue']"
        :max-cost-errors="errorsDict['maxCostUsd']"
        :max-cost-bot-offer-errors="errorsDict['maxCostBotOfferId']"
      ></cost-model>
    </div>
  </div>
</template>

<script setup lang="ts">
import CostModel from '@/views/reports/forms/campaign/components/cost-model.vue';
import WorkspaceSelect from '@/views/reports/pickers/wokrspace-select.vue';
import NoTagFound from '@/views/reports/views/no-tag-found.vue';
import {
  useCampaignPanelOne,
  type CampaignPanelOneProps,
} from './useCampaignPanelOne';
import { useCampaignFlowSelectOptions } from './useCampaignFlowSelectOptions';

const props = defineProps<CampaignPanelOneProps>();

const { fetchOfferOptions } = useCampaignFlowSelectOptions(() => ({
  workspaceId: String(props.form.workspaceId || ''),
  countryId: props.form.countryId,
}));

const emit = defineEmits<{
  'change-tracking-domain': [value: string];
  'change-workspace': [value: string];
  'change-country': [value: unknown];
}>();

const {
  store,

  t,
  getIntroParams,
  trafficSourceSelectOptionsWithNone,
  fetchTrafficSourcesOptions,
  countryListPlusGlobal,
  nameDescription,
  trackingDomains,
  uniquenessPeriod,
  usePostback,
  postbackPercent,
  useTrafficLoss,
  trafficLossPercent,
  customConversions,
  showPlanLimitsWarning_uniqness,
  showPlanLimitsWarning_usePostback,
  showPlanLimitsWarning_useTrafficLoss,
  showPlanLimitsWarning_tags,
  FFconversionNotificationUrlEnabled,
  useClientHintsItems,
  botFiltersModeItems,
  currencyListOrderedPreferred,
  offerOptions,
  onChangeTrackingDomain,
  onChangeWorkspace,
  onChangeCountry,
  clearTags,
  onSendCustomConversionsClick,
  resetPostback,
  resetTrafficLoss,
  resetUniquenessPeriod,
} = useCampaignPanelOne(props, emit);
</script>

<style lang="scss" scoped>
@import '@/styles/dialogs/report-modals';

  .name-description {
    margin-left: 16px;
    font-style: italic;
    font-size: 12px;
    word-break: break-all;
  }

.currency-selected {
  font-size: 12px;
}

.checkbox-container {
  :deep(.ui-label) {
    font-weight: bold;
    color: var(--color-graphite);
  }
}

.info-label {
  font-weight: bold!important;
  color: var(--color-graphite)!important;
  left: 0!important;
}

.label-slot {
  :deep(.v-input--selection-controls__input) {
    right: 0!important;
  }
}

.currency-input {
  :deep(.v-input) {
    margin-top: 0;
  }
}

.plan-limits-warning__uniquenessPeriod,
.plan-limits-warning__trafficLoss,
.plan-limits-warning__sendPostback{
  position: relative;
  &:before{
    display:block;
    content:'';
    border:1px solid var(--color-red);
    border-radius: 2px;
    position: absolute;
    top: -3px;
    left: -8px;
    right: -8px;
    bottom: 8px;
  }
}

.plan-limits-warning__uniquenessPeriod{
  &:before{
    bottom: -4px;
  }
}

.plan-limits-warning__sendPostback{
  &:before{
    bottom: 5px;
    top: -2px;
  }
}

.plan-limits-warning__trafficLoss{
  &:before{
    top: -2px;
    bottom: 5px;
  }
}

.uniquenessPeriod{
  position: relative;
  z-index: 1;
}

</style>
